import { Box, Button, Chip, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useLang } from '../../../language';
import { InputText } from '../../../components/Inputs/InputText';
import { EmpresasServices } from '../controllers/services/empresas.services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheckCircle, faEraser, faSearch, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { EMPRESA_DETALLE_PATHS } from '../controllers/empresaDetalle.routes';

export const ListadoEmpresas = ({ refreshKey = 0 }: any) => {

    const Lang = useLang()
    const navigate = useNavigate()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const [search, setSearch] = useState('')
    const [searchApplied, setSearchApplied] = useState('')

    const [fetchDisponiblesGET, fetchDisponiblesGETData] = EmpresasServices.GET_DISPONIBLES()

    useEffect(() => {

        fetchDisponiblesGET({
            params: {
                // El backend lee `page` (1-based), `per_page` y `search`
                page: page + 1,
                per_page: rowsPerPage,
                search: searchApplied || undefined,
            }
        })

    }, [page, rowsPerPage, refreshKey, searchApplied])

    const handleInputSearch = (e: any) => {
        setSearch(e.target.value)
    }

    const handleBuscar = (e?: any) => {

        if (e) e.preventDefault()

        setSearchApplied(search.trim())
        setPage(0)
    }

    const handleLimpiar = () => {

        setSearch('')
        setSearchApplied('')
        setPage(0)
    }

    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <Paper className='p-3 p-md-4'>

                <Typography variant='h6' className="my-0 pb-1 fw-500 color-primary">
                    {Lang('BUSCAR_EMPRESA')}
                </Typography>

                <Box
                    component="form"
                    onSubmit={handleBuscar}
                    className='mt-3'
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'flex-end' },
                        gap: { xs: 1.5, sm: 1 },
                        justifyContent: 'flex-end',
                    }}
                >
                    <Box sx={{ flex: '1 1 300px' }}>
                        <InputText
                            label={Lang('BUSCAR_EMPRESA')}
                            name='search'
                            value={search}
                            onChange={handleInputSearch}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            size="small"
                            disabled={fetchDisponiblesGETData.loading}
                            startIcon={<FontAwesomeIcon icon={faSearch} />}
                        >
                            {Lang('REALIZAR_BUSQUEDA')}
                        </Button>

                        <Button
                            type="button"
                            color="primary"
                            variant="outlined"
                            size="small"
                            onClick={handleLimpiar}
                            startIcon={<FontAwesomeIcon icon={faEraser} />}
                        >
                            {Lang('LIMPIAR')}
                        </Button>
                    </Box>
                </Box>

            </Paper>

            <Paper className='p-3 p-md-4 mt-3'>

                <Typography variant='h6' className="my-0 pb-1 fw-500 color-primary">
                    {Lang('LISTADO_EMPRESAS')}
                </Typography>

                <TableContainer className='mt-3'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{Lang('NOMBRE_DE_EMPRESA')}</TableCell>
                                <TableCell>{Lang('CIF')}</TableCell>
                                <TableCell>{Lang('EMAIL')}</TableCell>
                                <TableCell>{Lang('ESTADO')}</TableCell>
                                <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {
                                fetchDisponiblesGETData.loading &&

                                Array.from({ length: 5 }).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell width={160}><Skeleton variant="rounded" height={32} /></TableCell>
                                        <TableCell align="right" width={120}><Skeleton variant="rounded" height={32} /></TableCell>
                                    </TableRow>
                                ))
                            }

                            {
                                fetchDisponiblesGETData.data?.items?.map((item: any) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.nombre}</TableCell>
                                        <TableCell>{item.cif}</TableCell>
                                        <TableCell>{item.email}</TableCell>
                                        <TableCell>
                                            {
                                                item.estado === 'autorizada' ?
                                                    <Chip
                                                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                                        label={Lang('EMPRESA_AUTORIZADA')}
                                                        color="success"
                                                    /> :
                                                    item.estado === 'rechazada' ?
                                                        <Chip
                                                            icon={<FontAwesomeIcon icon={faTimesCircle} />}
                                                            label={Lang('EMPRESA_RECHAZADA')}
                                                            color="error"
                                                        /> :
                                                        item.estado === 'pendiente' ?
                                                            <Chip
                                                                label={Lang('PENDIENTE')}
                                                                color="warning"
                                                            /> :
                                                            <Chip
                                                                label={Lang('EMPRESA_NO_AUTORIZADA')}
                                                            />
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            {
                                                // El botón se pinta siempre para no romper
                                                // la retícula de la columna, pero solo es
                                                // utilizable si la empresa está autorizada:
                                                // mientras esté pendiente de resolver la
                                                // solicitud el acceso al detalle da 403, así
                                                // que el botón aparece deshabilitado.
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="contained"
                                                    startIcon={<FontAwesomeIcon icon={faArrowRight} />}
                                                    disabled={item.estado !== 'autorizada'}
                                                    onClick={() => navigate(`/empreses/${item.id}${EMPRESA_DETALLE_PATHS.INICI}`)}
                                                >
                                                    {Lang('ENTRAR')}
                                                </Button>
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))
                            }

                            {
                                !fetchDisponiblesGETData.loading && fetchDisponiblesGETData.data?.items?.length === 0 &&

                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Box sx={{ textAlign: 'center', py: 5 }}>
                                            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                                {Lang('SIN_RESULTADOS')}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={fetchDisponiblesGETData.data?.numeroTotalElementos || 0}
                    page={fetchDisponiblesGETData.data?.numeroPagina || 0}
                    onPageChange={handleChangePage}
                    rowsPerPage={fetchDisponiblesGETData.data?.numeroElementos || rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={Lang('FILAS_POR_PAGINA')}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${Lang('DE')} ${count}`}
                />

            </Paper>
        </Box>
    )
}